import React, { useEffect } from "react";
import "./Cookiespolicy.css";
import { useLocation } from "react-router-dom";
import SEO from "./SEO";

const PrivacypolicyPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      document.querySelector(location.hash)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location]);

  return (
    <>
      <SEO
        title="Privacy Policy | Waggex Payroll Software"
        description="Read the Waggex Privacy Policy to understand how we collect, use, and protect your personal and business information while using our payroll and HR software."
        keywords="Privacy policy, Waggex privacy policy, Payroll software privacy, Data protection policy, User data security policy, HR software privacy terms"
        canonical="https://www.waggex.com/privacy-policy"
      />
      <section id="CookiespolicyPage">
        <div className="container">
          <div className="cookies-policy-container">
            <h1 className="text-center">Privacy Policy </h1>
            <p>
              <strong>Effective Date:</strong> October 30, 2025
              <br />
              <strong>Last Updated:</strong> October 30, 2025
            </p>
            <h2>1. Introduction and Scope</h2>

            <p>
              Welcome to <strong>Waggex</strong>, a cloud-based Human Resource
              Management System (HRMS) that provides payroll, attendance,
              employee management, recruitment, leave management, expense
              management, performance management, and workforce automation
              solutions.
            </p>
            <p>
              Waggex is owned and operated by{" "}
              <strong>
                Digital Engineering Excellence Programming ("DEEP")
              </strong>
              .
            </p>
            <p>
              This Privacy Policy explains how{" "}
              <strong>Digital Engineering Excellence Programming</strong>,
              through its <strong>Waggex</strong> platform ("Waggex," "we,"
              "our," or "us"), collects, uses, stores, processes, and protects
              your information when you access our website, mobile applications,
              software, or related services (collectively, the "Services").
            </p>
            <p>
              By accessing or using the Services, you consent to the practices
              described in this Privacy Policy.
            </p>
            <p>
              This Privacy Policy does not apply to third-party websites or
              services that are not owned or controlled by Digital Engineering
              Excellence Programming.
            </p>

            {/* 2. Information We Collect */}
            <h2>2. Information We Collect</h2>
            <p>
              To provide and improve our Services, we collect the following
              categories of information.
            </p>

            <h5>2.1.Information You Provide</h5>
            <p>
              <strong>Organization Information</strong>
            </p>
            <ul>
              <li>Company Name </li>
              <li>Organization Details</li>
              <li> GST/PAN (where applicable)</li>
              <li>Business Address </li>
              <li>Billing Information </li>
              <li>Administrator Details</li>
            </ul>
            <p>
              <strong>Employee Information</strong>
            </p>
            <p>
              Depending on the modules used by your organization, we may
              collect:
            </p>
            <ul>
              <li>Employee Name</li>
              <li>Employee ID </li>
              <li>Email</li>
              <li> Address</li>
              <li> Mobile Number</li>
              <li> Date of Birth </li>
              <li>Address </li>
              <li>Department </li>
              <li>Designation </li>
              <li>Salary Information </li>
              <li>Attendance Records </li>
              <li>Leave Information</li>
              <li> Payroll Details</li>
              <li> Bank Account Details</li>
              <li> Tax Information</li>
              <li> Employment Records</li>
            </ul>
            <p>
              <strong>Customer Support Information</strong>
            </p>
            <p>
              Information voluntarily shared when contacting our support team.
            </p>
            <h5>2.2. Information Collected Automatically</h5>
            <p>
              We automatically collect certain technical information, including:
            </p>
            <ul>
              <li>IP Address</li>
              <li> Browser Type</li>
              <li> Device Information</li>
              <li> Operating System L</li>
              <li>ogin History </li>
              <li>Usage Statistics</li>
              <li> Error Logs </li>
              <li>Session Information</li>
            </ul>
            <h5>2.2. Cookies & Tracking Technologies</h5>
            <p>We use cookies and similar technologies to:</p>
            <ul>
              <li>Authenticate users </li>
              <li> Maintain secure sessions </li>
              <li>Remember preferences </li>
              <li>Improve website performance </li>
              <li>Analyze website usage </li>
              <li>Enhance user experience</li>
            </ul>

            {/* 3. How We Use Your Information */}
            <h2>3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li>Provide HRMS Services </li>
              <li>Process Payroll</li>
              <li> Manage Attendance </li>
              <li>Generate Payslips</li>
              <li> Manage Leave </li>
              <li>Manage Recruitment</li>
              <li> Verify Attendance </li>
              <li>Process Expenses </li>
              <li>Generate Reports </li>
              <li>Improve Product Performance </li>
              <li>Provide Customer Support </li>
              <li>Send Service Notifications </li>
              <li>Prevent Fraud </li>
              <li>Ensure Security </li>
              <li>Comply with Legal Requirements</li>
            </ul>

            {/* 4. How We Share Your Information */}
            <h2>4.Sharing of Information</h2>
            <p>We do not sell your personal information.</p>
            <p>Information may be shared only when necessary with:</p>
            <p>
              <strong>Service Providers</strong>
            </p>
            <p>
              Including cloud hosting providers, payment gateways, SMS
              providers, email providers, analytics providers, and
              infrastructure partners.
            </p>
            <p>
              <strong>Government Authorities</strong>
            </p>
            <p>When legally required under applicable laws.</p>
            <p>
              <strong>Business Transfers</strong>
            </p>
            <p>
              In the event of a merger, acquisition, restructuring, or sale of
              business assets.
            </p>
            <p>
              <strong>Customer Organizations</strong>
            </p>
            <p>
              Where employee information is managed by the employer using
              Waggex.
            </p>

            {/* 5. Data Security */}
            <h2>5. Data Security</h2>
            <p>We implement industry-standard security practices including:</p>
            <ul>
              <li>Secure HTTPS Encryption</li>
              <li> Data Encryption</li>
              <li> Role-Based Access Control </li>
              <li>Password Encryption </li>
              <li>Firewall Protection </li>
              <li>Secure Cloud Infrastructure </li>
              <li>Backup & Disaster Recovery</li>
              <li> Security Monitoring</li>
              <li> Access Logging</li>
            </ul>
            <p>
              Although we use commercially reasonable security measures, no
              online system can guarantee absolute security.
            </p>

            {/* 6. Biometric / Face Data Collection and Use */}
            <h2>6. FaceLens Biometric Data</h2>
            <p>
              Waggex FaceLens is designed solely for employee attendance
              verification.
            </p>
            <p>
              When enabled by an organization, FaceLens may capture facial
              information for identity verification during employee attendance.
            </p>
            <p>
              Face data is <strong>never used</strong> for advertising,
              profiling, or public identification.
            </p>
            {/* 6. Biometric / Face Data Collection and Use */}

            {/* 7. What Face Data We Collect */}
            <h2>7.Face Data We Collect</h2>
            <p>Where enabled by your organization, FaceLens may collect:</p>

            <ul>
              <li>Live Facial Image </li>
              <li>Employee Profile Image </li>
              <li>Facial Template </li>
              <li> Attendance Verification Result </li>
              <li> Date & Time </li>
              <li>Device Information</li>
            </ul>
            <p>
              We collect only the minimum biometric information required for
              attendance verification.
            </p>
            {/* 7. What Face Data We Collect */}

            {/* 8. What Face Data We Collect */}
            <h2>8. How Face Data Is Used</h2>
            <p>Face data is used only to:</p>
            <ul>
              <li>Verify employee identity</li>
              <li>Prevent proxy attendance</li>
              <li>Authenticate attendance events</li>
              <li>Improve attendance accuracy</li>
              <li>Secure attendance records</li>
            </ul>
            <p>Face data is never used for:</p>
            <ul>
              <li>Marketing</li>
              <li>Advertising</li>
              <li>Facial Profiling</li>
              <li>Public Identification</li>
              <li>AI Model Training</li>
              <li>Selling to Third Parties</li>
            </ul>

            <h2>9. Face Data Sharing</h2>
            <p>Face data is shared only when necessary with:</p>
            <ul>
              <li>Customer Organization</li>
              <li>Secure Cloud Infrastructure Providers</li>
              <li>Authorized Waggex Systems</li>
            </ul>
            <p>
              We never sell, rent, lease, or monetize biometric information.
            </p>

            {/* 10. Storage and Security */}
            <h2>10.Face Data Storage & Retention</h2>

            <p>
              Face data is stored using secure encryption and protected by
              strict access controls.
            </p>
            <p>Generally:</p>

            <ul>
              <li>
                Attendance images are retained only as long as operationally
                required.
              </li>
              <li>
                Facial templates remain active while an employee is enrolled.
              </li>
              <li>
                Face data is deleted when:
                <ul>
                  <li>The employee is removed.</li>
                  <li>The organization requests deletion.</li>
                  <li>The account is closed.</li>
                  <li>Legal retention obligations expire.</li>
                </ul>
              </li>
            </ul>

            {/* 11. Retention of Face Data */}
            <h2>11. User & Organization Controls</h2>
            <p>
              Organizations using FaceLens are responsible for obtaining any
              employee notices or consents required by applicable law.
            </p>
            <p>Authorized administrators may:</p>

            <ul>
              <li>Add employees</li>
              <li>Remove employees</li>
              <li>Delete biometric records</li>
              <li>Request data deletion</li>
            </ul>
            <h2>12. Cookies</h2>
            <p>We use cookies for:</p>
            <ul>
              <li>Authentication</li>
              <li>Security</li>
              <li>Performance</li>
              <li>Analytics</li>
              <li>User Preferences</li>
            </ul>
            <p>
              Users may disable cookies through browser settings; however,
              certain features may not function properly.
            </p>

            {/* 13. Use of Cookies and Trackin*/}
            <h2>13.Your Privacy Rights</h2>
            <p>Depending on applicable law, you may have the right to:</p>
            <p>You have the right to:</p>
            <ul>
              <li>Access your data</li>
              <li>Correct inaccurate information</li>
              <li>Delete your information</li>
              <li>Restrict processing</li>
              <li>Object to processing</li>
              <li>Withdraw consent where applicable</li>
              <li>Request a copy of your data</li>
            </ul>

            <p>Requests can be submitted through our support team.</p>

            {/* 14. Your Data Protection Rights */}
            <h2>14.Data Retention</h2>
            <p>We retain information only for as long as necessary to:</p>
            <ul>
              <li>Provide services</li>
              <li>Meet legal obligations</li>
              <li>Resolve disputes</li>
              <li>Enforce agreements</li>
            </ul>
            <p>Data is securely deleted once retention requirements expire.</p>

            <h2>15.Children's Privacy</h2>
            <p>
              Our Services are intended for businesses and individuals aged{" "}
              <strong>18</strong> years or older.
            </p>
            <p>
              We do not knowingly collect personal information from children
              under 18.
            </p>
            <h2>16.Changes to this Privacy Policy</h2>

            <p>We may update this Privacy Policy periodically.</p>
            <p>
              Material updates will be posted on our website along with the
              revised "Last Updated" date.
            </p>
            <p>
              Continued use of the Services constitutes acceptance of the
              updated Privacy Policy.
            </p>

            <h2>17.Misuse Disclaimer</h2>

            <p>
              Waggex provides software tools for payroll processing, HR
              management, attendance management, and payslip generation.
            </p>
            <p>
              Users are solely responsible for the accuracy and legality of the
              information entered into the platform.
            </p>
            <p>
              Creating fake payslips, misrepresenting employee information,
              impersonating organizations, or engaging in fraudulent activities
              using Waggex is strictly prohibited.
            </p>
            <p>
              <strong> Digital Engineering Excellence Programming </strong>acts
              solely as the software provider and shall not be liable for
              unlawful use of the platform by its users.
            </p>
            <h2>18.Brand Ownership</h2>

            <p>
              <strong> Waggex </strong>is a registered trademark and software
              platform owned and operated by{" "}
              <strong> Digital Engineering Excellence Programming. </strong>
            </p>
            <p>
              All references to "Waggex" in this Privacy Policy refer to the
              software products and services provided by Digital Engineering
              Excellence Programming.
            </p>
            <h2>19. Contact Us</h2>
            <p>
              If you have any questions regarding these Terms, please contact
              us:
            </p>
            <p>
              <strong>
                {" "}
                Digital Engineering Excellence Programming (DEEP)
              </strong>{" "}
            </p>
            <p>
              <strong> Operating Brand: Waggex</strong>
            </p>
            <p>
              <strong>Address:</strong> Phase 8-B, Industrial Area, Sector 74,
              Sahibzada Ajit Singh Nagar, Punjab – 140307, India
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:support@waggex.com" rel="canonical">
                support@waggex.com
              </a>
            </p>
           
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacypolicyPage;
